/* eslint-disable camelcase */
/* global google, cssInJS */
// @ts-nocheck
import React from 'react';
import PropTypes from 'react-proptypes';
import Immutable from 'immutable';
import { RepeatableValue, Feature, Record } from '@fulcrumapp/fulcrum-core';
import MapView from './map-view';
import SearchPlugin from '../../shared/map/plugins/search';
import { AppContext } from '../../hooks/use-app';
import ArcGisMapView from '../../shared/map/arc-gis-map-view';
import Repeatables from '../../shared/map/plugins/repeatables';
import { shouldUseEsriMaps } from '../../sketch-editor';
import RepeatableInteractivity from '../../shared/map/plugins/repeatable-interactivity';
import GraphicForExtents from './utils/graphic-for-extents';
import LayerChangeCoordinator from '../../shared/map/plugins/utils/layer-change-coordinator';
import LayerPickerPlugin from '../../shared/map/plugins/layer-picker-plugin';
import StandardLayers from '../../shared/map/plugins/standard-layers';
import GeoJsonLayers from '../../shared/map/plugins/geo-json-layers';
import PortalLayers from '../../shared/map/plugins/portal-layers';
import FeatureFocusTracker from '../../shared/map/plugins/feature-focus-tracker';
import Basemap from '../../shared/map/plugins/basemap';
import DefaultPlugins from '../../shared/map/plugins/default';
import LayerSettingsReferenceHandler from '../../shared/map/plugins/utils/layer-settings-reference-handler';
import DebuggingLayers from '../../shared/map/plugins/debugging-layers';
import PublicFeatureServiceLayers from '../../shared/map/plugins/public-feature-service-layers';

export default class RepeatableMapView extends React.Component {
  static propTypes = {
    editable: PropTypes.bool.isRequired,
    feature: PropTypes.instanceOf(Feature).isRequired,
    repeatableValue: PropTypes.instanceOf(RepeatableValue).isRequired,
    visible: PropTypes.bool.isRequired,
    onFeatureSelect: PropTypes.func.isRequired,
    highlightedListFeature: PropTypes.instanceOf(Feature),
    getHighlightedFeature: PropTypes.func,
    setHoveredFeature: PropTypes.func,
    levelId: PropTypes.string,
    statusColor: PropTypes.string,
  };

  static contextType = AppContext;

  static defaultProps = {
    visible: true,
  };

  private layerChangeCoordinator;
  private layerSettingsReferenceHandler;

  componentWillUnmount() {
    this.layerChangeCoordinator?.destroy();
  }

  render() {
    const { styles } = RepeatableMapView;
    const {
      editable, repeatableValue, feature, highlightedListFeature, visible, levelId,
    } = this.props;

    const attrs = {
      height: '',
    };

    const circles = [];
    const features = [];
    // eslint-disable-next-line camelcase
    const { globals } = this.context;
    // eslint-disable-next-line camelcase
    const esriEnabled = shouldUseEsriMaps(globals, repeatableValue.element);
    this.addParentGeometry(feature, circles, features, esriEnabled);

    let graphic;

    repeatableValue._items.forEach((item) => {
      this.addRepeatableItemGeometry(
        item,
        item.id === highlightedListFeature?.id,
        circles,
        features,
        esriEnabled,
      );
    });

    let bounds = null;

    if (esriEnabled) {
      graphic = this.graphic(features);
    }

    const extents = this.context.preferences.lastMapExtents;

    if (circles.length) {
      bounds = new google.maps.LatLngBounds();
      circles.forEach((circle) => bounds.extend({ lat: circle.location.lat, lng: circle.location.lng }));
    }

    const repeatableMarkers = new Immutable.List(Immutable.fromJS(circles));

    const legacyView = (
      <MapView
        style={attrs}
        className={styles.map}
        visible={visible}
        repeatableMarkers={repeatableMarkers}
        highlightedRepeatable={highlightedListFeature}
        onRepeatableMarkerAdded={this.handleRepeatableMarkerAdded}
        bounds={bounds}
        extents={extents}
        onMarkerClick={this.handleMarkerClick}
        onMarkerMouseOver={this.handleMarkerMouseOver}
        onMarkerMouseOut={this.handleMarkerMouseOut}
        parentView="repeatable-list-view"
      />
    );

    return (
      <div className={styles.container}>
        {esriEnabled ? this.buildMapView(
          extents,
          features,
          editable,
          levelId,
          graphic,
          this.onFeatureHover,
          this.onFeatureClick,
          this.props.getHighlightedFeature,
        ) : legacyView}
      </div>
    );
  }

  buildMapView = (
    extents, features, editable, levelId, graphic,
  ) => {
    const { getHighlightedFeature, statusColor } = this.props;
    const { onFeatureHover, onFeatureClick } = this;
    // eslint-disable-next-line camelcase
    const { layerSettings, api: { token }, globals: { config: { esri_api_key, google_maps } } } = this.context;
    if (!this.layerSettingsReferenceHandler) {
      this.layerSettingsReferenceHandler = LayerSettingsReferenceHandler(layerSettings);
    }
    if (!this.layerChangeCoordinator) {
      this.layerChangeCoordinator = LayerChangeCoordinator(this.layerSettingsReferenceHandler);
    }

    const plugins = [
      Basemap(this.layerSettingsReferenceHandler, google_maps.api_key),
      DefaultPlugins(),
      SearchPlugin(),
      LayerPickerPlugin('repeatables', editable),
      StandardLayers(this.layerChangeCoordinator, token),
      GeoJsonLayers(this.layerChangeCoordinator),
      PortalLayers(this.layerChangeCoordinator, esri_api_key),
      PublicFeatureServiceLayers(this.layerChangeCoordinator),
      Repeatables(features, statusColor),
      RepeatableInteractivity(onFeatureHover, onFeatureClick, getHighlightedFeature, statusColor),
      FeatureFocusTracker(graphic, this.context.preferences),
    ];

    if (window.localStorage.getItem('layer-coordination-debugging') === 'true') {
      plugins.push(DebuggingLayers());
    }

    return (
      <ArcGisMapView
        extents={extents}
        parentView="repeatable-list-view"
        plugins={plugins}
      />
    );
  };

  // Add geometry for a repeatable item. Modifies the circles / features arrays
  addRepeatableItemGeometry = (item, shouldHighlight, circles, features, esriEnabled) => {
    const highlightColor = [33, 150, 243];
    const highlightedColor = shouldHighlight
      ? highlightColor
      : undefined;
    const geometry = { ...item.geometryAsGeoJSON, item };
    if (esriEnabled && geometry) {
      features.push({
        id: item.id,
        geometry,
        data: { feature: item, color: highlightedColor },
      });
    }

    if (!esriEnabled && item.hasCoordinate) {
      const location = { lat: item.latitude, lng: item.longitude };

      circles.push({
        data: { feature: item },
        title: item.displayValue,
        location,
      });
    }
  };

  // Adds the geometry for the parent record to the circles / features arrays
  addParentGeometry = (feature, circles, features, esriEnabled) => {
    const geometry = feature.geometryAsGeoJSON;
    if (esriEnabled && geometry) {
      features.push({
        id: feature.id,
        geometry,
        data: { feature, parent: true },
      });
    }

    if (!esriEnabled && feature.hasCoordinate) {
      const { latitude, longitude } = feature;
      const location = { lat: latitude, lng: longitude };
      const circle = {
        data: { feature, parent: true },
        location,
      };
      circles.push(circle);
    }

    return [circles, features];
  };

  graphic = (features) => {
    const { lastEditorMapExtents } = this.context.preferences;

    return GraphicForExtents(features, lastEditorMapExtents);
  };

  handleRepeatableMarkerAdded = (marker) => {
    marker.addListener('click', () => {
      this.handleMarkerClick(marker);
    });
  };

  handleMarkerClick = (marker) => {
    const { data } = marker;

    // don't fire a selection event for clicking on the parent
    if (!data.parent) {
      this.props.onFeatureSelect(data.feature);
    }
  };

  onFeatureHover = (item: Record<string, any> | null) => {
    this.props.setHoveredFeature(item);
  };

  onFeatureClick = (item: Record<string, any> | null) => {
    this.props.onFeatureSelect(item);
  };
}

const styles = cssInJS({
  container: {
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  map: {
    width: '100%',
    height: '100%',
  },
});

RepeatableMapView.styles = styles;
